import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, IconButton, Chip, MenuItem, TextField, Typography } from '@mui/material';
import EmptyState from '../ui/EmptyState';
import PriceTag from '../ui/PriceTag';
import './CartModal.css';

const CAR_COLOR_OPTIONS = [
  'white', 'black', 'silver', 'gray', 'red', 'blue', 'green',
  'yellow', 'orange', 'brown', 'beige', 'gold', 'other',
];

const PlusIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>;
const MinusIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13H5v-2h14v2z" /></svg>;
const CloseIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L10.59 13.41 4.29 19.71 2.88 18.29 9.17 12 2.88 5.71 4.29 4.29l6.3 6.3 6.3-6.29z" /></svg>;
const TrashIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"/></svg>;
const BagIcon = () => <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 6h-2c0-2.76-2.24-5-5-5S7 3.24 7 6H5C3.9 6 3 6.9 3 8v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM12 3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3z" /></svg>;

const UNDO_WINDOW_MS = 4000;

export default function CartModal({
  cart,
  currency,
  totalPrice,
  taxBreakdown,
  taxLabel,
  placingOrder,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onPlaceOrder,
  getItemSubtotal,
  getImageUrl,
  orderType,
  carNumber = '',
  carColor = '',
  onCarNumberChange,
  onCarColorChange,
}) {
  const { t } = useTranslation();
  const showTaxBreakdown = Boolean(taxBreakdown?.tax_active && taxBreakdown.tax_amount > 0);
  const taxInclusive = Boolean(taxBreakdown?.tax_inclusive);
  const itemCount = cart.reduce((sum, i) => sum + (i.quantity || 0), 0);
  const isCarOrder = orderType === 'car';
  const carFieldsValid = !isCarOrder || carNumber.trim().length > 0;

  /* Inline-undo: when a user taps the trash, we paint a confirm row for
     UNDO_WINDOW_MS instead of removing immediately. Undo cancels the timer;
     timeout fires the actual onRemoveItem.

     Keyed by the cart item REFERENCE (not by index) so that:
     (a) when one pending item commits, later items' positions shift but
         their pending state stays attached to the same logical row;
     (b) onRemoveItem(item) on the parent uses identity-filtering, which
         is shift-proof when two timers fire in sequence. The previous
         index-based approach lost an item in that scenario.

     The pendingRemoves state holds a Map<itemRef, { name }> for the
     render lookup. Timer IDs live in a parallel Map<itemRef, timerId> on
     a ref so the unmount-cleanup effect can clear them without depending
     on state changes. */
  const [pendingRemoves, setPendingRemoves] = useState(() => new Map());
  const timersRef = useRef(new Map());
  const closeBtnRef = useRef(null);
  const openerRef = useRef(null);

  /* Capture the element that had focus when the cart opened, so we can
     return focus there on close (typically the cart FAB). */
  useEffect(() => {
    openerRef.current = document.activeElement;
    // Move focus into the dialog on next tick so a screen reader announces it.
    const rafId = window.requestAnimationFrame(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    });
    return () => {
      window.cancelAnimationFrame(rafId);
      // Return focus to opener on unmount.
      if (openerRef.current && typeof openerRef.current.focus === 'function') {
        try { openerRef.current.focus(); } catch (_) { /* opener may have unmounted */ }
      }
    };
  }, []);

  // Esc closes the cart.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Clear any in-flight undo timers on actual unmount only.
  useEffect(() => () => {
    timersRef.current.forEach(id => id && clearTimeout(id));
    timersRef.current.clear();
  }, []);

  const handleTrash = useCallback((item, name) => {
    const timerId = setTimeout(() => {
      timersRef.current.delete(item);
      setPendingRemoves(prev => {
        const next = new Map(prev);
        next.delete(item);
        return next;
      });
      onRemoveItem(item);
    }, UNDO_WINDOW_MS);
    timersRef.current.set(item, timerId);
    setPendingRemoves(prev => {
      const next = new Map(prev);
      next.set(item, { name });
      return next;
    });
  }, [onRemoveItem]);

  const handleUndo = useCallback((item) => {
    const id = timersRef.current.get(item);
    if (id) clearTimeout(id);
    timersRef.current.delete(item);
    setPendingRemoves(prev => {
      const next = new Map(prev);
      next.delete(item);
      return next;
    });
  }, []);

  return (
    <div className="cart-modal" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="cart-title">
      <div className="cart-content" onClick={(e) => e.stopPropagation()}>
        <div className="cart-drag-handle" aria-hidden="true" />
        <div className="cart-header">
          <div className="cart-header-text">
            <h2 id="cart-title">{t('menu.cart.title')}</h2>
            {itemCount > 0 && (
              <span className="cart-header-count" aria-live="polite">
                {t('menu.cart.itemsCount', { count: itemCount, defaultValue: `${itemCount} item${itemCount === 1 ? '' : 's'}` })}
              </span>
            )}
          </div>
          <IconButton
            ref={closeBtnRef}
            onClick={onClose}
            aria-label={t('common.close')}
            className="cart-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="cart-items">
          {cart.length === 0 ? (
            <EmptyState
              icon={<BagIcon />}
              title={t('menu.cart.emptyTitle', 'Your cart is empty')}
              description={t('menu.cart.emptyDescription', 'Pick something delicious from the menu and it will show up here.')}
            />
          ) : (
            cart.map((item, index) => {
              const pending = pendingRemoves.get(item);
              if (pending) {
                return (
                  <div key={`pending-${item.id}-${index}`} className="cart-item cart-item--pending" role="status">
                    <span className="cart-item-pending-label">
                      {t('menu.cart.removed', { item: pending.name, defaultValue: `Removed “${pending.name}”` })}
                    </span>
                    <Button
                      size="small"
                      onClick={() => handleUndo(item)}
                      className="cart-item-undo-btn"
                    >
                      {t('menu.cart.undo', 'Undo')}
                    </Button>
                  </div>
                );
              }
              const thumb = item.image_url && getImageUrl ? getImageUrl(item.image_url) : null;
              const hasAddons = item.selectedAddons && item.selectedAddons.length > 0;
              return (
                <div
                  key={`${item.id}-${index}-${hasAddons ? JSON.stringify(item.selectedAddons) : ''}`}
                  className="cart-item"
                >
                  <div className="cart-item-thumb" aria-hidden="true">
                    {thumb ? (
                      <img src={thumb} alt="" loading="lazy" />
                    ) : (
                      <span className="cart-item-thumb-placeholder">🍽️</span>
                    )}
                  </div>
                  <div className="cart-item-info">
                    <div className="cart-item-name">{item.name}</div>
                    {hasAddons && (
                      <div className="cart-item-addons">
                        {item.selectedAddons.map((a, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={
                              a.price > 0 ? (
                                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.25em' }}>
                                  {a.option} +
                                  <PriceTag amount={a.price} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                                </span>
                              ) : (
                                a.option
                              )
                            }
                            sx={{
                              backgroundColor: 'var(--surface-tertiary)',
                              color: 'var(--text-secondary)',
                              fontSize: 'var(--text-xs)',
                              height: 24,
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <div className="cart-item-price-row">
                      <span className="cart-item-unit-price">
                        <PriceTag amount={item.price} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} /> × {item.quantity}
                      </span>
                      <PriceTag amount={getItemSubtotal(item)} currency={currency} size="sm" />
                    </div>
                  </div>
                  <div className="cart-item-controls">
                    <div className="cart-item-stepper" role="group" aria-label={t('menu.cart.quantity', 'Quantity')}>
                      <IconButton
                        size="small"
                        onClick={() => onUpdateQuantity(index, -1)}
                        aria-label={t('menu.cart.decreaseQuantity')}
                        className="cart-item-stepper-btn"
                      >
                        <MinusIcon />
                      </IconButton>
                      <span className="cart-item-qty" aria-live="polite">{item.quantity}</span>
                      <IconButton
                        size="small"
                        onClick={() => onUpdateQuantity(index, 1)}
                        aria-label={t('menu.cart.increaseQuantity')}
                        className="cart-item-stepper-btn"
                      >
                        <PlusIcon />
                      </IconButton>
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => handleTrash(item, item.name)}
                      aria-label={t('menu.cart.removeItem', { item: item.name, defaultValue: `Remove ${item.name}` })}
                      className="cart-item-trash-btn"
                    >
                      <TrashIcon />
                    </IconButton>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {cart.length > 0 && (
          <div className="cart-footer">
            {isCarOrder && (
              <Box className="cart-car-form" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" className="cart-car-form-title">
                  {t('menu.cart.carDetails')}
                </Typography>
                <p className="cart-car-form-help">
                  {t('menu.cart.carNoteHelp', "We'll bring your order to your car.")}
                </p>
                <div className="cart-car-form-grid">
                  <TextField
                    label={t('menu.cart.carNumber')}
                    value={carNumber}
                    onChange={(e) => onCarNumberChange && onCarNumberChange(e.target.value)}
                    size="small"
                    fullWidth
                    required
                    inputProps={{ maxLength: 20 }}
                    autoComplete="off"
                    className="cart-car-form-number"
                  />
                  <TextField
                    select
                    label={t('menu.cart.carColor')}
                    value={carColor}
                    onChange={(e) => onCarColorChange && onCarColorChange(e.target.value)}
                    size="small"
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    className="cart-car-form-color"
                  >
                    <MenuItem value="">
                      <em>{t('menu.cart.carColorNone')}</em>
                    </MenuItem>
                    {CAR_COLOR_OPTIONS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`menu.cart.carColors.${key}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </Box>
            )}
            <div className="cart-totals">
              {showTaxBreakdown && !taxInclusive && (
                <>
                  <div className="cart-total cart-total--secondary">
                    <span>{t('tax.subtotal')}</span>
                    <PriceTag amount={taxBreakdown.subtotal} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                  </div>
                  <div className="cart-total cart-total--secondary">
                    <span>{t('tax.taxLine', { label: taxLabel || 'VAT', rate: taxBreakdown.tax_rate })}</span>
                    <PriceTag amount={taxBreakdown.tax_amount} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                  </div>
                </>
              )}
              <div className="cart-total cart-total--grand">
                <span>{t('menu.cart.total')}</span>
                <PriceTag amount={totalPrice} currency={currency} size="lg" />
              </div>
              {showTaxBreakdown && taxInclusive && (
                <div className="cart-total cart-total--inclusive-note">
                  {t('menu.cart.taxIncludedNote', {
                    label: taxLabel || 'VAT',
                    rate: taxBreakdown.tax_rate,
                    defaultValue: `Includes ${taxLabel || 'VAT'} ${taxBreakdown.tax_rate}%`,
                  })}
                </div>
              )}
            </div>
            <Button
              variant="pill"
              fullWidth
              onClick={onPlaceOrder}
              disabled={placingOrder || !carFieldsValid}
              startIcon={placingOrder ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : null}
            >
              {t('menu.cart.placeOrder')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
